import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import {
    selectedPrescripteursSelector,
    selectedStatusPrescripteursSelector,
    setIdPrescripteur,
    useIdPrescripteurCustomizer,
    useTiersService,
    useTiersServiceSelector,
} from '@europrocurement/l2d-domain';
import _ from 'lodash';
import { useFullLayout } from '@europrocurement/flexy-components/components/templates/FullLayout/FullLayoutContext';

interface UsePrescriberOptions {
    prescriberIdFromParent?: number;
}

interface UsePrescriberReturn {
    prescriberId: number | undefined;
    prescriber: ReturnType<typeof selectedPrescripteursSelector> extends (state: infer S) => infer R
        ? R
        : never;
    prescriberIds: number[] | undefined;
}

const usePrescriber = ({
    prescriberIdFromParent,
}: UsePrescriberOptions = {}): UsePrescriberReturn => {
    const keycloak = UseKeycloakService();
    const dispatch = useDispatch();
    const prescriberIdCustomizer = useIdPrescripteurCustomizer();
    const [prescriberId, setPrescriberId] = useState<number | undefined>(prescriberIdCustomizer);
    const [prescriberIds, setPrescriberIds] = useState<number[] | undefined>([]);

    const prescriber = useSelector(selectedPrescripteursSelector);

    const { selectPrescripteur } = useTiersService();

    const { jobName, setJobName } = useFullLayout();

    const status = useTiersServiceSelector(selectedStatusPrescripteursSelector);

    useEffect(() => {
        if (prescriberIdFromParent !== undefined) {
            setPrescriberId(prescriberIdFromParent);
            return;
        }

        if (!keycloak.isInitialized || !keycloak.isAutenticated || !keycloak.getTokenParsed()) {
            return;
        }

        const tokenParsed = keycloak.getTokenParsed();
        const pId: string[] | null = tokenParsed?.IdPrescripteur || null;
        const mainPId: string | null = tokenParsed?.MainIdPrescripteur || null;
        if (status === 'idle') {
            if ((!pId && prescriberId !== undefined) || pId?.length === 0) {
                setPrescriberIds([]);
                setPrescriberId(undefined);
                return;
            }
            if (pId) {
                if (!prescriberIdCustomizer || pId.indexOf(`${prescriberIdCustomizer}`) === -1) {
                    const selectedPrescriberId = mainPId || pId[0];
                    dispatch(selectPrescripteur({ id: selectedPrescriberId }));
                    setPrescriberId(Number(selectedPrescriberId));
                    setPrescriberIds(pId.map(Number));
                    dispatch(setIdPrescripteur(Number(selectedPrescriberId)));
                } else if (!_.isEqual(Number(prescriber?.id), Number(prescriberIdCustomizer))) {
                    dispatch(selectPrescripteur({ id: prescriberIdCustomizer }));
                    setPrescriberId(prescriberIdCustomizer);
                    setPrescriberIds(pId.map(Number));
                }
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keycloak, status, prescriberIdFromParent]);

    useEffect(() => {
        if (prescriber) {
            const currentContact = prescriber.contacts.find(
                (contact) =>
                    contact.emailsContact.find((email) => email.email === keycloak.getEmail()) !==
                    undefined,
            );
            if (currentContact && currentContact.fonction && currentContact.fonction !== jobName) {
                setJobName(currentContact.fonction);
            }
        }
    }, [jobName, keycloak, prescriber, setJobName]);

    return { prescriberId, prescriber, prescriberIds };
};

export default usePrescriber;
